// Explore: every trace in the range, newest first, read through one search.
// The field narrows them (words become filters, shown before they apply); the
// list is the traces themselves; "Analyze this scope" opens a chat beside the
// list that writes about the traces the search reaches, pins the ones it names
// to the top of the list, and suggests filters that apply as turns of the chat.
// A trace opens its board over the page.
import { useCallback, useMemo, useRef, useState } from "react";
import DateRangePicker from "../components/DateRangePicker.jsx";
import AnalysisPanel from "../components/explore/AnalysisPanel.jsx";
import ExploreQuery from "../components/explore/ExploreQuery.jsx";
import TraceList from "../components/explore/TraceList.jsx";
import { useElementSize } from "../components/kernelPrimitives/shared.jsx";
import Button from "../components/ui/Button.jsx";
import { useQuery } from "../lib/dataClient.js";
import { DATE_RANGE_PRESETS } from "../lib/dateRangePresets.js";
import { buildFacts } from "../lib/exploreAnalysis.js";
import { addClauses } from "../lib/exploreTranslate.js";
import { useSessionFacts, useTraceDetails } from "../lib/sessionFacts.js";
import { useSessionTitles } from "../lib/sessionTitles.js";
import { toApiRange } from "../lib/shell.js";
import { previewTraceCanvas } from "../lib/traceCanvasStore.js";
import { tokenCostEnabled } from "../lib/tokenCost.js";
import { useTraceCosts } from "../lib/traceCosts.js";
import { useSearchScope, useSessionMatch, useWorkspaceSearch } from "../lib/workspaceSearch.js";

const NUM = new Intl.NumberFormat("en-US");
const NONE = Object.freeze([]);
// The rows the list shows at first, and adds at a time.
const PAGE = 100;
// From this width the analysis sits beside the list. Narrower, it takes the
// width under the search, and the list returns when it closes. Measured, not
// a container query: the panel's lever menu is fixed to the viewport.
const BESIDE_FROM = 740;

// The range in words, for the analysis: a preset's name, else its dates.
function rangeName(range) {
  const preset = DATE_RANGE_PRESETS.find((p) => p.id === range);
  if (preset) return preset.summary;
  const span = toApiRange(range);
  return span ? `${String(span.from).slice(0, 10)} to ${String(span.to).slice(0, 10)}` : "the selected range";
}

// The traces the analysis pins that the list does not hold (older than its
// newest), read as the list's rows are.
function usePinnedScope(ids, range) {
  const q = ids.length ? `session:${ids.join(",")}` : "";
  const match = useSessionMatch(range, q, { enabled: !!q });
  return useMemo(() => ({ q, ready: !!q && !!match.data, scopePath: [], steer: "", match, capped: false, sessions: match.data?.sessions || null }), [q, match]);
}

export default function ExplorePage({ range, onRangeChange = null }) {
  const [search, setSearch] = useWorkspaceSearch();
  const scope = useSearchScope(range);
  const read = useSessionFacts(scope, range);
  // The new scope's read failed: its traces are unknown, not the last scope's.
  const unread = !!(read.previous && read.failed);
  const found = unread ? NONE : read.sessions || NONE;
  const counted = useQuery("getOverviewMetrics", { scopePath: [], range, query: scope.q, byTrace: true }, { enabled: scope.ready });
  const countFailed = !!counted.error && !counted.loading;
  const total = unread || (countFailed && counted.previousParams) ? null : counted.data?.sessions ?? null;

  const [open, setOpen] = useState(false);
  const [pins, setPins] = useState(NONE);
  const [lit, setLit] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const body = useRef(null);
  const bodyWidth = useElementSize(body).width;
  const stacked = open && bodyWidth > 0 && bodyWidth < BESIDE_FROM;

  const listed = useMemo(() => new Map(found.map((r) => [r.id, r])), [found]);
  const missing = useMemo(() => pins.filter((id) => !listed.has(id)), [pins, listed]);
  const pinRead = useSessionFacts(usePinnedScope(missing, range), range);
  const pinnedFound = useMemo(() => {
    const extra = new Map((pinRead.sessions || NONE).map((r) => [r.id, r]));
    return pins.map((id) => listed.get(id) || extra.get(id)).filter(Boolean);
  }, [pins, listed, pinRead.sessions]);

  // Filled in (who ran each, what review found, its title and cost): the rows
  // the list shows and the pinned ones; with the analysis open, every row it
  // is written from. The rows are drawn at once and each part lands as it comes.
  const filling = useMemo(() => {
    const seen = new Set();
    return [...pinnedFound, ...(open ? found : found.slice(0, shown))].filter((r) => !seen.has(r.id) && seen.add(r.id));
  }, [pinnedFound, found, open, shown]);
  const ids = useMemo(() => filling.map((r) => r.id), [filling]);
  const details = useTraceDetails(filling, range);
  const withDetails = useCallback((r) => ({ ...r, ...details.get(r.id) }), [details]);
  const rows = useMemo(() => found.map(withDetails), [found, withDetails]);
  const pinned = useMemo(() => pinnedFound.map(withDetails), [pinnedFound, withDetails]);
  const span = useMemo(() => {
    const starts = filling.map((r) => r.startMs).filter(Number.isFinite);
    const ends = filling.map((r) => r.lastMs).filter(Number.isFinite);
    return starts.length && ends.length ? { from: Math.min(...starts), to: Math.max(...ends) } : null;
  }, [filling]);
  const titles = useSessionTitles(ids, span);
  const costs = useTraceCosts(ids, range);

  // The scope's own rows and count, not the last scope's while its are read:
  // an analysis written then would be about the scope just left. A count that
  // failed is left out rather than waited for.
  const current = !read.previous && (countFailed || (!counted.previousParams && total != null));
  const factsReady = open && current && read.status === "done"
    && rows.every((r) => titles.has(r.id) && (!tokenCostEnabled() || costs.has(r.id)) && r.person !== undefined && r.bySev !== undefined);
  const facts = useMemo(
    () => (factsReady ? buildFacts({ rows, titles, costs, total, query: scope.q, range: rangeName(range), capped: !!read.capped }) : null),
    [factsReady, rows, titles, costs, total, scope.q, range, read.capped],
  );
  const scopeKey = `${scope.q}|${typeof range === "string" ? range : JSON.stringify(range)}`;

  const filter = useCallback((clause) => setSearch((cur) => addClauses(cur, clause)), [setSearch]);
  const openRow = useCallback((row, title) => previewTraceCanvas({ id: row.id, title: title || "" }), []);
  const openTrace = useCallback((id) => previewTraceCanvas({ id, title: titles.get(id) || "" }), [titles]);
  const close = useCallback(() => {
    setOpen(false);
    setPins(NONE);
    setLit(null);
  }, []);

  return (
    <div data-explore-page="" className="relative flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)] px-[var(--main-inset)] pb-[var(--main-inset)] text-[color:var(--ink)]">
      <header className="flex min-h-[62px] shrink-0 flex-wrap items-center gap-x-[12px] gap-y-[8px] py-[8px]">
        <h1 className="m-0 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em]">Explore</h1>
        <span className="text-[13px] tabular-nums text-[color:var(--muted)]" aria-busy={total == null ? "true" : undefined} data-explore-count="">
          {total != null ? <><span className="font-medium text-[color:var(--ink)]">{NUM.format(total)}</span> {total === 1 ? "trace" : "traces"}</> : null}
        </span>
        <span className="flex-1" />
        {onRangeChange ? (
          <span className="w-[160px] shrink-0">
            <DateRangePicker range={range} onRangeChange={onRangeChange} />
          </span>
        ) : null}
      </header>
      <div ref={body} className={`flex min-h-0 flex-1 ${stacked ? "flex-col" : ""}`}>
        <div className={`flex min-w-0 flex-col ${stacked ? "" : "flex-1"}`}>
          <div className="flex gap-[10px]">
            <ExploreQuery search={search} onSearch={setSearch} range={range} />
            {open ? null : <Button variant="primary" size="lg" onClick={() => setOpen(true)}>Analyze this scope</Button>}
          </div>
          {stacked ? null : <TraceList rows={rows} pinned={unread ? NONE : pinned} titles={titles} costs={costs} status={unread ? "error" : read.status} shown={shown} onMore={() => setShown((n) => n + PAGE)} lit={lit} onOpen={openRow} onFilter={filter} />}
        </div>
        {open ? (
          <div className={stacked ? "mt-[18px] min-h-0 flex-1" : "ml-[16px] w-[400px] shrink-0"}>
            <AnalysisPanel
              facts={facts}
              scopeKey={scopeKey}
              scopePath={scope.scopePath}
              status={current && total != null ? `Analyzing ${NUM.format(total)} ${total === 1 ? "trace" : "traces"}` : "Analyzing"}
              failed={unread}
              onClose={close}
              onPins={setPins}
              onFilter={filter}
              onOpenTrace={openTrace}
              onLightTrace={setLit}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
